import React, { useEffect, useState } from 'react'
import { Box, CardMedia, Typography } from '../../Material/index'

import GppMaybeIcon from '@mui/icons-material/GppMaybe'
import base64ToBlob from 'pages/simulation/register/documents/util/base64ToBlob'
import { getDocument, GlobalWorkerOptions, PDFDocumentProxy } from 'pdfjs-dist'
import 'pdfjs-dist/build/pdf.worker.entry'

// Configurar o caminho do worker do PDF.js
GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.js'

// Definir a interface para as propriedades do componente
interface CustomPdfViewerProps {
  file?: string
  disableScroll?: boolean
  mock?: boolean // Defina o tipo correto ou remova se não for usado
}

// Estilos do componente
const useStyles = {
  container: {
    height: '100vh',
    width: '100%',
    backgroundColor: '#f5f5f5',
    overflow: 'auto',
    padding: '16px'
  },
  viewer: {
    width: '100%',
    height: 'auto',
    border: 'none',
    backgroundColor: 'white'
  },
  pageContainer: {
    marginBottom: '5px',
    boxShadow: '0 3px 6px rgba(0, 0, 0, 0.1)',
    backgroundColor: 'white',
    border: '1px solid #e0e0e0',
    position: 'relative'
  },
  pageNumber: {
    position: 'absolute',
    fontFamily: 'BB-Font-Regular',
    top: '8px',
    right: '8px',
    fontSize: '11px',
    color: '#979393',
    padding: '2px 6px',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    borderRadius: '4px'
  }
}

export const CustomPdfViewer: React.FC<CustomPdfViewerProps> = ({ disableScroll, file }) => {
  const [pageImages, setPageImages] = useState<string[]>([])

  useEffect(() => {
    if (file) {
      const blob = base64ToBlob(file, 'application/pdf')
      const blobUrl = URL.createObjectURL(blob)
      renderPdfToImages(blobUrl)

      // Limpar o blob URL quando o componente for desmontado
      return () => {
        URL.revokeObjectURL(blobUrl)
      }
    }
  }, [file])

  // Função para renderizar uma página do PDF para uma imagem Data URL
  const renderPage = async (pdf: PDFDocumentProxy, pageNum: number): Promise<string> => {
    const page = await pdf.getPage(pageNum)
    const viewport = page.getViewport({ scale: 3 })
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')

    if (!context) {
      throw new Error('Não foi possível obter o contexto 2D do canvas.')
    }

    canvas.height = viewport.height
    canvas.width = viewport.width

    const renderContext = {
      canvasContext: context,
      viewport
    }

    await page.render(renderContext).promise
    return canvas.toDataURL()
  }

  // Função para renderizar todo o PDF para imagens
  const renderPdfToImages = async (pdfUrl: string) => {
    try {
      const loadingTask = getDocument(pdfUrl)
      const pdf = await loadingTask.promise
      const numPages = pdf.numPages
      const images: string[] = []

      for (let i = 1; i <= numPages; i++) {
        const image = await renderPage(pdf, i)
        images.push(image)
      }

      setPageImages(images)
    } catch (error) {
      console.error('Erro ao renderizar o PDF:', error)
    }
  }

  // Renderizar mensagem quando não há arquivo e nenhuma imagem está disponível
  if (!file && pageImages.length === 0) {
    return (
      <Box className="flex items-center justify-center w-full h-full">
        <GppMaybeIcon fontSize="large" />
        <Typography className="text-center ml-2">Nenhum arquivo encontrado</Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        ...useStyles.container,
        overflow: disableScroll ? 'hidden' : 'auto'
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {pageImages.map((src, index) => (
          <Box key={index} sx={useStyles.pageContainer}>
            <CardMedia
              title={`Página ${index + 1}`}
              component="img"
              src={src}
              alt={`Página ${index + 1}`}
            />
            <Typography sx={useStyles.pageNumber}>página {index + 1}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )
}
